import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IHome } from '@/modules/home/types';
import { IContracts } from '@/modules/workforce/Contracts/types';
import axiosClient from '@/shared/lib/axios';

/************************************ CHECK SUPPLIERS *********************************************/
export const checkSuppliersCount = async (): Promise<{
  totalCount: number;
}> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/supplier-assignment/suppliers-count`
  );
  return data;
};

export const useCheckSuppliersCount = () => {
  return useQuery({
    queryKey: ['checkSuppliersCount'],
    queryFn: () => checkSuppliersCount(),
  });
};
/************************************ CONTRACTS LIST *********************************************/
export const getContractsList = async (
  params: IContracts.IContractsListParams
): Promise<{
  supplierAssignments: IContracts.IContractsListSchema[];
  totalCount: number;
}> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/supplier-assignments`,
    { params }
  );
  return data;
};

export const useGetContractsList = (
  params: IContracts.IContractsListParams
) => {
  return useQuery({
    queryKey: ['getContractsList', params],
    queryFn: () => getContractsList(params),
    enabled: !!params?.entityId && !!params?.pageNumber && !!params?.status,
  });
};
/************************************ JOBS LIST *********************************************/
export const getJobsList = async (
  params: IContracts.IJobsListParams
): Promise<IContracts.IJobsListSchema[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/supplier-assignment/jobs`,
    { params }
  );
  return data;
};

export const useGetJobsList = (params: IContracts.IJobsListParams) => {
  return useQuery({
    queryKey: ['getJobsList', params],
    queryFn: () => getJobsList(params),
    enabled: !!params.entityId && !!params.supplierId,
  });
};
/************************************ SUPPLIERS LIST *********************************************/
export const getSuppliersList = async (
  params: IContracts.ISuppliersListParams
): Promise<IHome.IGetInputsResponse<IContracts.ISupplierSchema>> => {
  const { data } = await axiosClient.get<IContracts.ISuppliersListSchema>(
    `/company/portalInsights/supplier-assignment/suppliers`,
    { params }
  );
  return {
    items: data.suppliers,
    totalItems: data.totalCount,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    nextPage:
      params.pageNumber * params.pageSize < data?.totalCount
        ? params.pageNumber + 1
        : undefined,
  };
};
export const useGetSuppliersList = (
  params: IContracts.ISuppliersListParams
) => {
  return useInfiniteQuery({
    queryKey: ['getSuppliersList', params],
    queryFn: ({ pageParam }) =>
      getSuppliersList({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: Boolean(params.entityId),
  });
};
/************************************ UNITS LIST *********************************************/
export const getUnitsList = async (
  entityId: number
): Promise<IContracts.IUnit[]> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/supplier-assignment/measurement-units/${entityId}`
  );
  return data;
};
export const useGetUnitsList = (entityId: number) => {
  return useQuery({
    queryKey: ['getUnitsList', entityId],
    queryFn: () => getUnitsList(entityId),
    enabled: !!entityId,
  });
};
/************************************ ADD CONTRACT *********************************************/
export const addNewContract = async (
  params: IContracts.IAddContractAPISchema
) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/supplier-assignment`,
    params
  );
  return data;
};

export const useAddNewContract = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['addNewContract'],
    mutationFn: addNewContract,
    onSuccess: () => {
      queryClient.invalidateQueries(['getContractsList']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
    },
  });
};

/************************************ CONTRACT DETAILS *********************************************/
const getContractDetails = async (
  params: IContracts.IContractEntityIdentifier
): Promise<IContracts.IContractDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/supplier-assignments/${params.supplierAssignmentId}`,
    {
      params: {
        entityId: params.entityId,
      },
    }
  );
  return data;
};

export const useGetContractDetails = (
  params: IContracts.IContractEntityIdentifier
) => {
  return useQuery({
    queryKey: ['getContractDetails', params],
    queryFn: () => getContractDetails(params),
    enabled: !!params.supplierAssignmentId && !!params.entityId,
  });
};

/************************************ END OR DELETE CONTRACT *********************************************/
const endDeleteontract = async (
  params: IContracts.IEndDeleteContractParams
) => {
  const endpoint =
    params.method === 'delete'
      ? `/company/portalInsights/supplier-assignment/${params.entityId}/${params.supplierAssignmentId}`
      : '/company/portalInsights/end-supplier-assignment';

  const { data } = await axiosClient[params.method](
    endpoint,
    undefined,
    params.method === 'patch'
      ? {
          params: {
            entityId: params.entityId,
            supplierAssignmentId: params.supplierAssignmentId,
            todayDate: params.todayDate,
          },
        }
      : undefined
  );

  return data;
};

export const useEndDeleteontract = () => {
  return useMutation({
    mutationFn: endDeleteontract,
  });
};

/************************************ EDIT CONTRACT *********************************************/

const editContract = async ({
  supplierAssignmentId,
  ...obj
}: IContracts.IEditContractAPISchema) => {
  const { data } = await axiosClient.put(
    `/company/portalInsights/supplier-assignment/${supplierAssignmentId}`,
    obj
  );
  return data;
};

export const useEditContract = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editContract,
    onSuccess: () => {
      queryClient.invalidateQueries(['getContractsList']);
      queryClient.invalidateQueries(['getContractDetails']);
    },
  });
};

/************************************ EDIT CONTRACT WITH MISMATCH  *********************************************/

const resolveContractUnitMismatch = async ({
  supplierAssignmentId,
  ...obj
}: IContracts.IResolveUnitMismatchParams) => {
  const { data } = await axiosClient.put(
    `/company/portalInsights/supplier-assignment/price/${supplierAssignmentId}`,
    obj
  );
  return data;
};

export const useResolveContractUnitMismatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resolveContractUnitMismatch,
    onSuccess: () => {
      queryClient.invalidateQueries(['getContractsList']);
      queryClient.invalidateQueries(['getContractDetails']);
    },
  });
};
